import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiCheck, FiX, FiInfo, FiDownload, FiTrendingUp } = FiIcons;

export default function Details() {
  const { id } = useParams();
  const location = useLocation();
  const { getProviderById, getAllProviders, renderValue } = useData();
  const [dataset, setDataset] = useState(null);
  const [allProviderData, setAllProviderData] = useState([]);
  const [marktart, setMarktart] = useState('');
  const [energieart, setEnergieart] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const markt = params.get('markt') || '';
    const energie = params.get('energie') || '';
    
    setMarktart(markt);
    setEnergieart(energie);
    
    const data = getProviderById(id);
    setDataset(data);
    
    // Alle Daten für Cross-Referenz laden
    const allData = getAllProviders();
    setAllProviderData(allData);
  }, [id, location.search, getProviderById, getAllProviders]);

  if (!dataset) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiTrendingUp} className="text-gray-400 text-4xl mb-4 mx-auto" />
        <p className="text-gray-500 mb-4">Datensatz nicht gefunden.</p>
        <Link to="/" className="inline-flex items-center px-4 py-2 bg-[#9b061b] text-white rounded-lg hover:bg-[#7a0516] transition-colors">
          <SafeIcon icon={FiArrowLeft} className="mr-2" />
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  // Zusatzdaten aus der jeweils anderen Marktart finden
  const getOtherMarketData = () => {
    const otherMarkt = marktart === 'terminmarkt' ? 'Spotmarkt' : 'Terminmarkt';
    return allProviderData.find(p => 
      p.evu === dataset.evu && 
      p.marktart === otherMarkt &&
      (energieart === 'strom' ? p.bietet_strom : p.bietet_gas)
    );
  };

  const otherMarketData = getOtherMarketData();

  // Haupt-Sektionen definieren
  const mainSections = [
    {
      title: 'Stammdaten',
      icon: FiInfo,
      items: [
        { label: 'EVU', value: renderValue(dataset.evu) },
        { label: 'Marktart', value: renderValue(dataset.marktart) },
        { label: 'Energieart', value: energieart === 'strom' ? 'Strom' : 'Gas' },
        { label: 'Bietet Strom', value: dataset.bietet_strom ? 'Ja' : 'Nein' },
        { label: 'Bietet Gas', value: dataset.bietet_gas ? 'Ja' : 'Nein' },
        { label: 'Quelle', value: renderValue(dataset.quelle) }
      ]
    },
    {
      title: `${energieart === 'strom' ? 'Strom' : 'Gas'}-Daten`,
      icon: FiTrendingUp,
      items: [
        { label: energieart === 'strom' ? 'Strom SLP' : 'Gas SLP', value: renderValue(energieart === 'strom' ? dataset.strom_slp : dataset.gas_slp) },
        { label: energieart === 'strom' ? 'Strom RLM' : 'Gas RLM', value: renderValue(energieart === 'strom' ? dataset.strom_rlm : dataset.gas_rlm) },
        { label: 'Verbrauchsgrenzen', value: renderValue(energieart === 'strom' ? dataset.verbrauchsgrenzen_strom : dataset.verbrauchsgrenzen_gas) }
      ]
    },
    {
      title: 'Vertragsdetails',
      icon: FiCheck,
      items: [
        { label: 'Bestellungsrelevante Unterlagen', value: renderValue(dataset.bestellungsrelevante_unterlagen) },
        { label: 'Pflichtangaben', value: renderValue(dataset.pflichtangaben) },
        { label: 'Vertragsunterlagen nach Abschluss', value: renderValue(dataset.vertragsunterlagen_nach_abschluss) },
        { label: 'Ausgeschlossene Branchen', value: renderValue(dataset.ausgeschlossene_branchen) }
      ]
    },
    {
      title: 'Konditionen',
      icon: FiDownload,
      items: [
        { label: 'Provisionsgrenze', value: renderValue(dataset.provisionsgrenze) },
        { label: 'Auszahlung', value: renderValue(dataset.auszahlung) },
        { label: 'Bonitätsgrenze', value: renderValue(dataset.bonitaetsgrenze) },
        { label: 'Bemerkung', value: renderValue(dataset.bemerkung) }
      ]
    },
    {
      title: 'Mehr-/Mindermengen',
      icon: FiInfo,
      items: [
        // FIX: Hier lesen wir jetzt strikt das neue Feld "mehr_mindermengen_in_prozent"
        { label: 'Mehr-/Mindermengen in %', value: renderValue(dataset.mehr_mindermengen_in_prozent) }
      ]
    },
    {
      title: 'Vertragsbedingungen',
      icon: FiCheck,
      items: [
        { label: 'Kündigungsfrist', value: renderValue(dataset.kuendigungsfrist) },
        { label: 'Verlängerung', value: renderValue(dataset.verlaengerung_aktiv_passiv) },
        { label: 'Bündelung', value: renderValue(dataset.buendelung) },
        { label: 'Vollmachten erlaubt', value: renderValue(dataset.vollmachten_erlaubt) },
        { label: 'Wechsel möglich', value: renderValue(marktart === 'terminmarkt' ? dataset.wechsel_von_fest_zu_spot : dataset.wechsel_von_spot_zu_fest) }
      ]
    },
    {
      title: 'Provisionsregelung',
      icon: FiDownload,
      items: [
        { label: 'SPOT Provision', value: renderValue(dataset.provision_spot) },
        { label: 'Festpreis Provision', value: renderValue(dataset.provision_festpreis) },
        { label: 'Tranche', value: renderValue(dataset.provision_tranche) },
        { label: 'Provisions-Bemerkung', value: renderValue(dataset.provision_bemerkung_festpreis) }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/" className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Zurück
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-[#1e314b]">{renderValue(dataset.evu)}</h1>
            <p className="text-gray-600 flex items-center space-x-2">
              <SafeIcon icon={FiTrendingUp} />
              <span>{renderValue(dataset.marktart)} • {energieart === 'strom' ? 'Strom' : 'Gas'}</span>
            </p>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            console.log(`Download: ${dataset.evu} Konditionen`);
          }}
          className="inline-flex items-center px-4 py-2 bg-[#1e314b] text-white rounded-lg hover:bg-[#152738] transition-colors"
        >
          <SafeIcon icon={FiDownload} className="mr-2" />
          Konditionen herunterladen
        </motion.button>
      </div>

      {/* Übersichtskarte */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="bg-gradient-to-r from-[#9b061b] to-[#1e314b] rounded-xl text-white p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold mb-2">{renderValue(dataset.marktart)}</div>
            <div className="text-sm opacity-90">Marktart</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold mb-2">{energieart === 'strom' ? 'Strom' : 'Gas'}</div>
            <div className="text-sm opacity-90">Energieart</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold mb-2">{renderValue(dataset.provisionsgrenze) || '–'}</div>
            <div className="text-sm opacity-90">Provisionsgrenze</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold mb-2">
              {renderValue(dataset.mehr_mindermengen_in_prozent) || '–'}
            </div>
            <div className="text-sm opacity-90">Mehr-/Mindermengen</div>
          </div>
        </div>
      </motion.div>

      {/* Haupt-Sektionen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {mainSections.map((section, sectionIndex) => (
          <motion.div 
            key={sectionIndex} 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: sectionIndex * 0.1 }} 
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-[#9b061b] text-white w-10 h-10 rounded-lg flex items-center justify-center">
                <SafeIcon icon={section.icon} />
              </div>
              <h3 className="text-lg font-semibold text-[#1e314b]">{section.title}</h3>
            </div>
            <div className="space-y-4">
              {section.items.map((item, itemIndex) => (
                <div 
                  key={itemIndex} 
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0"
                >
                  <span className="text-sm font-medium text-gray-700">{item.label}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">
                      {item.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Zusatzdaten aus anderer Marktart */}
      {otherMarketData && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.4 }} 
          className="bg-blue-50 border border-blue-200 rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-blue-800 mb-4">
            Zusatzdaten aus {otherMarketData.marktart}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-blue-700 mb-3">{energieart === 'strom' ? 'Strom' : 'Gas'}-Daten</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">SLP:</span>
                  <span className="font-medium">{renderValue(energieart === 'strom' ? otherMarketData.strom_slp : otherMarketData.gas_slp)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">RLM:</span>
                  <span className="font-medium">{renderValue(energieart === 'strom' ? otherMarketData.strom_rlm : otherMarketData.gas_rlm)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Verbrauchsgrenzen:</span>
                  <span className="font-medium">{renderValue(energieart === 'strom' ? otherMarketData.verbrauchsgrenzen_strom : otherMarketData.verbrauchsgrenzen_gas)}</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-blue-700 mb-3">Konditionen</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Mehr-/Mindermengen in %:</span>
                  <span className="font-medium">{renderValue(otherMarketData.mehr_mindermengen_in_prozent)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Provisionsgrenze:</span>
                  <span className="font-medium">{renderValue(otherMarketData.provisionsgrenze)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Kündigungsfrist:</span>
                  <span className="font-medium">{renderValue(otherMarketData.kuendigungsfrist)}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Hinweise */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.4 }} 
        className="bg-blue-50 border border-blue-200 rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-blue-800 mb-4">Hinweise</h3>
        <div className="space-y-3 text-sm text-gray-700">
          <div className="flex items-start space-x-2">
            <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p>Dies sind die Konditionen des Anbieters aus dem aktuellen Leitfaden.</p>
          </div>
          <div className="flex items-start space-x-2">
            <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p>Die Daten stammen aus der Datei: {renderValue(dataset.quelle)}</p>
          </div>
          <div className="flex items-start space-x-2">
            <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p>Alle Angaben werden 1:1 aus den Originaldateien übernommen ohne Interpretation.</p>
          </div>
        </div>
      </motion.div>

      {/* Aktions-Buttons */}
      <div className="flex flex-wrap gap-4">
        <Link 
          to={`/comparison?ids=${dataset.id}&markt=${marktart}&energie=${energieart}`} 
          className="inline-flex items-center px-6 py-3 bg-[#9b061b] text-white rounded-lg hover:bg-[#7a0516] transition-colors font-medium"
        >
          <SafeIcon icon={FiTrendingUp} className="mr-2" />
          Vergleich starten
        </Link>
        <button 
          onClick={() => {
            console.log(`Kontakt zu ${dataset.evu} aufnehmen`);
          }}
          className="inline-flex items-center px-6 py-3 border border-[#1e314b] text-[#1e314b] rounded-lg hover:bg-[#1e314b] hover:text-white transition-colors font-medium"
        >
          <SafeIcon icon={FiInfo} className="mr-2" />
          Kontakt aufnehmen
        </button>
      </div>
    </div>
  );
}